import React, { useEffect, useState } from "react";
import api from "../utils/api";
import { URLS } from "../utils/urls";
import { History, Search, Calendar, Eye, X } from "lucide-react";
import { useTopLoader } from "../context/TopLoaderContext";

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const { start, stop } = useTopLoader();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        start();
        setLoading(true);
        const res = await api.get(URLS.AUDIT_LOGS);
        setLogs(res.data);
      } catch (err) {
        console.error("Failed to fetch audit logs:", err);
      } finally {
        setLoading(false);
        stop();
      }
    };
    fetchLogs();
  }, []);

  const getActionBadge = (action) => {
    const colors = {
      LOGIN: "bg-blue-100 text-blue-700",
      VERIFY_OTP: "bg-blue-100 text-blue-700",
      CREATE_PERSONALITY: "bg-green-100 text-green-700",
      UPDATE_PERSONALITY: "bg-yellow-100 text-yellow-700",
      DELETE_PERSONALITY: "bg-red-100 text-red-700",
      CREATE_EVENT: "bg-green-100 text-green-700",
      UPDATE_EVENT: "bg-yellow-100 text-yellow-700",
      DELETE_EVENT: "bg-red-100 text-red-700",
      APPROVE_EVENT: "bg-green-100 text-green-700",
      DELETE_USER: "bg-red-100 text-red-700",
      BLOCK_USER: "bg-red-100 text-red-700",
      UPDATE_USER: "bg-yellow-100 text-yellow-700",
      UPDATE_SETTINGS: "bg-yellow-100 text-yellow-700",
      UPDATE_SETTING: "bg-yellow-100 text-yellow-700",
      UPDATE_SETTINGS_BULK: "bg-yellow-100 text-yellow-700",
      BULK_NOTIFICATION: "bg-purple-100 text-purple-700",
      PARDON_USER: "bg-green-100 text-green-700",
      DISMISS_REPORT: "bg-gray-100 text-gray-700",
      CREATE_SONG: "bg-green-100 text-green-700",
      UPDATE_SONG: "bg-yellow-100 text-yellow-700",
      DELETE_SONGS: "bg-red-100 text-red-700",
      UPLOAD_MEDIA: "bg-blue-100 text-blue-700",
      DELETE_MEDIA: "bg-red-100 text-red-700",
      ADD_ICEBREAKER: "bg-green-100 text-green-700",
      DELETE_ICEBREAKER: "bg-red-100 text-red-700",
      CANCEL_SUBSCRIPTION_NOW: "bg-red-100 text-red-700",
      EXTEND_SUBSCRIPTION: "bg-blue-100 text-blue-700",
      CREATE_ADMIN: "bg-green-100 text-green-700",
      DELETE_ADMIN: "bg-red-100 text-red-700",
    };
    return colors[action] || "bg-gray-100 text-gray-700";
  };

  const formatDetails = (details) => {
    if (!details) return "-";
    try {
      if (typeof details === 'string' && (details.startsWith('{') || details.startsWith('['))) {
        const parsed = JSON.parse(details);
        return JSON.stringify(parsed);
      }
      return details;
    } catch (e) {
      return details;
    }
  };

  const renderModal = () => {
    if (!selectedLog) return null;

    let parsedDetails = selectedLog.details;
    try {
      if (typeof selectedLog.details === 'string' && (selectedLog.details.startsWith('{') || selectedLog.details.startsWith('['))) {
        parsedDetails = JSON.parse(selectedLog.details);
      }
    } catch (e) {}

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200 my-auto">
          <div className="p-6 border-b flex items-center justify-between bg-gray-50/50">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Audit Log Details</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getActionBadge(selectedLog.action)}`}>
                  {selectedLog.action.replace(/_/g, " ")}
                </span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-500 font-medium">#{selectedLog.id}</span>
              </div>
            </div>
            <button 
              onClick={() => setSelectedLog(null)}
              className="p-2 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-100 rounded-xl transition-all"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Administrator</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#db8654]/20 text-[#db8654] flex items-center justify-center font-bold text-xs">
                      {selectedLog.firstName?.[0]}{selectedLog.lastName?.[0]}
                    </div>
                    <div>
                      <p className="text-sm text-gray-900 font-bold leading-tight">{selectedLog.firstName} {selectedLog.lastName}</p>
                      <p className="text-xs text-gray-500">{selectedLog.email}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Timestamp</p>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar size={14} className="text-[#db8654]" />
                    <p className="text-sm font-semibold">{new Date(selectedLog.createdAt).toLocaleString()}</p>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Target Resource ID</p>
                  <div className="flex items-center gap-2">
                    <div className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs font-mono text-gray-600 break-all w-full">
                      {selectedLog.targetId || "System Level Action"}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider px-1">Action Metadata (JSON)</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-5 border border-gray-800 shadow-inner overflow-hidden">
                  <div className="overflow-x-auto">
                    <pre className="text-xs text-green-400/90 whitespace-pre-wrap break-all font-mono leading-relaxed custom-scrollbar">
                      {typeof parsedDetails === 'object' 
                        ? JSON.stringify(parsedDetails, null, 2) 
                        : parsedDetails || "No metadata provided"}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <button 
              onClick={() => setSelectedLog(null)}
              className="px-6 py-2 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition-all shadow-sm active:scale-95"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#db8654]/10 text-[#db8654] rounded-xl">
            <History size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Audit Logs</h2>
            <p className="text-gray-500 text-sm">Track all administrative actions</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Admin</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Action</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Target</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Details</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Date</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="6" className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-full"></div></td>
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No logs found</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-800">{log.firstName} {log.lastName}</span>
                        <span className="text-xs text-gray-500">{log.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getActionBadge(log.action)}`}>
                        {log.action.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100 text-gray-600 truncate max-w-[120px] block">
                        {log.targetId || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-[200px]">
                        <p className="text-xs text-gray-500 truncate font-mono">
                          {formatDetails(log.details)}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap">
                        <Calendar size={14} />
                        {new Date(log.createdAt).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => setSelectedLog(log)}
                        className="p-2 text-[#db8654] hover:bg-[#db8654]/10 rounded-lg transition-colors"
                        title="View Full Details"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {renderModal()}
    </div>
  );
};

export default AuditLogs;
